import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import * as _ from 'lodash';
import { BehaviorSubject, Subject, distinctUntilChanged, map, takeUntil } from 'rxjs';
import { BladeService } from 'src/app/modules/blades/services/blade.service';
import { APP_CONTEXT } from 'src/app/modules/core/models/app-state';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgClass, AsyncPipe } from '@angular/common';
import { VehicleStore } from 'src/app/modules/core/store';
import { MatBadgeModule } from '@angular/material/badge';
import { TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import { TrafficDeviationAccessService } from '@modules/core/services/traffic-deviation-access.service';
import { AuthService } from '@modules/core/services';
import { LoggingService } from '@modules/shared/services/logging.service';

interface AppContextMenuItem {
    context?: APP_CONTEXT;
    icon?: string;
    materialIcon?: string;
    bottom?: boolean;
    tooltip?: string;
    path?: string;
    resetOtherContext?: boolean;
    badge?: boolean;
    badgeText?: string;
    hasAccess?: 'TaWriter' | 'TemplateEditor' | 'HomeView';
}

@Component({
    selector: 'app-context-menu',
    templateUrl: './context-menu.component.html',
    styleUrls: ['./context-menu.component.scss'],
    imports: [MatTooltipModule, MatBadgeModule, NgClass, AsyncPipe, TranslateModule, RouterLink],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class ContextMenuComponent implements OnInit, OnDestroy {
    private readonly _appState = inject(AppStateService);
    private readonly _router = inject(Router);
    private readonly _bladeService = inject(BladeService);
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _trafficDeviationsStore = inject(TrafficDeviationsStore);
    private readonly _trafficDeviationAccessService = inject(TrafficDeviationAccessService);
    private readonly _authService = inject(AuthService);
    private readonly _logger = inject(LoggingService);

    public menuItems: AppContextMenuItem[] = [];
    public bottomMenuItems: AppContextMenuItem[] = [];
    public hasAccess: { [key: string]: boolean } = {};
    public currentContext$ = this._appState.getPropertyState('selectedContext');
    
    public readonly hasTrafficDeviationRegion$ = new BehaviorSubject(false);

    private readonly _ngUnsubscribe$ = new Subject<void>();


    constructor() {
        this._bladeService.allOutletParams$.pipe(takeUntil(this._ngUnsubscribe$)).subscribe(params => {
            if (!params) {
                return;
            }

            if (params['changeContext'] === 'true') {
                // Remove query params
                this._router.navigate([], {
                    queryParams: { changeContext: null },
                    queryParamsHandling: 'merge'
                });
                // if url has any references to a context -> navigate by context
                if (params['paths']) {
                    _.each(params['paths'], path => {
                        const foundContext = _.find(this.menuItems, item => item.path === path);
                        if (foundContext && foundContext.context !== this._appState.stateSnapshot.selectedContext) {
                            this.changeContext(foundContext);
                        }
                    });
                }
            }
        });
    }

    ngOnInit(): void {
        this._trafficDeviationAccessService.hasAccess().then(hasAccess => {
            this.hasAccess = hasAccess;
            this.hasAccess['HomeView'] = this._authService.hasAccessToOmsFunction('HomeView');
            this._setMenuItems();

            this._vehicleStore.processedWarnings$.pipe(
                distinctUntilChanged(_.isEqual),
                takeUntil(this._ngUnsubscribe$)
            ).subscribe(vehicleWarnings => {
                let warningCount = 0;
                _.each(vehicleWarnings, v => {
                    warningCount += v?.totalWarnings;
                });

                const tab = _.find(this.menuItems, t => t.context === APP_CONTEXT.WARNING);
                if (tab) {
                    tab.badge = warningCount > 0;
                    tab.badgeText = warningCount.toString();
                }
            });
            
            this._trafficDeviationsStore.trafficDeviations$.pipe(
                map((trafficDevations) => {
                    if (!trafficDevations) return [];

                    const currentTime = Date.now();
                    return trafficDevations.filter(deviation => {
                        if (deviation.isDeleted) return false;
                        
                        const startDate = this.getEarliestDate(
                            deviation.trafficDeviationScope.startDate, 
                            deviation.trafficDeviationScope.trafficDeviationInformation?.publicationStartDate
                        ).getTime();
                        const endDate = this.getLatestDate(
                            deviation.trafficDeviationScope.endDate,
                            deviation.trafficDeviationScope.trafficDeviationInformation?.publicationEndDate
                        ).getTime();

                        // Check if the deviation is currently active or is comming up
                        return startDate <= currentTime && endDate >= currentTime || startDate > currentTime;
                    });
                }),
                map((trafficDeviations) => trafficDeviations.length),
                takeUntil(this._ngUnsubscribe$)
            ).subscribe(amount => {
                const tab = _.find(this.menuItems, t => t.context === APP_CONTEXT.TRAFFIC_DEVIATIONS);
                if (tab) {
                    tab.badge = amount > 0;
                    tab.badgeText = amount.toString();
                }
            })
        }).catch(error => {
            this._logger.logException(error);
        });

        this._appState.getSelectedGaragesState().pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((garages) => {
            const hastusRegionCodes = garages.map(g => g.hastusRegionCode);
            const hasTrafficDeviationRegion = this._trafficDeviationAccessService.hasTrafficDeviationRegion(hastusRegionCodes);

            this.hasTrafficDeviationRegion$.next(hasTrafficDeviationRegion);
        });
    }

    ngOnDestroy(): void {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    public trackByFunction(index: number, item: any): string | number {
        if (item?.context) return item.context;
        if (item?.tooltip) return item.tooltip;
        
        return index;
    }

    public resetContext(): void {
        this._appState.changeContext(APP_CONTEXT.NONE);
    }

    // kunna hämta olika context? kunna ändra dessa?
    public async changeContext(item: AppContextMenuItem): Promise<void> {
        if (!item) return;

        if (item.context) {
            if (item.resetOtherContext) {
                const right0Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right0']?.segments.map(s => s.path) ?? null;
                const right1Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right1']?.segments.map(s => s.path) ?? null;
                const right2Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right2']?.segments.map(s => s.path) ?? null;
                /* Await to ensure navigation completes (including ngOnDestroy on
                   deactivated outlet components) before changing context. This prevents
                   the same component being destroyed and re-created in the wrong order. */
                await this._router.navigate(['', {
                    outlets: { outlet0: null, outlet1: null, 'outlet-right0': right0Segments, 'outlet-right1': right1Segments, 'outlet-right2': right2Segments }
                }]);
            }

            this._appState.changeContext(item.context);
        } else if (item.path) {
            if (this._router.url === item.path) {
                const right0Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right0']?.segments.map(s => s.path) ?? null;
                const right1Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right1']?.segments.map(s => s.path) ?? null;
                const right2Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right2']?.segments.map(s => s.path) ?? null;
                this._router.navigate(['', {
                    outlets: { outlet0: null, outlet1: null, 'outlet-right0': right0Segments, 'outlet-right1': right1Segments, 'outlet-right2': right2Segments }
                }]);
                return;
            }

            this._router.navigate(['', { outlets: { outlet0: [item.path], outlet1: null, outlet2: null }}]);
        }
    }

    private _setMenuItems(): void {
        this.menuItems = [];
        this.menuItems.push(
            {
                context: APP_CONTEXT.HOME,
                icon: 'fa-solid fa-house',
                tooltip: _translate('Home'),
                resetOtherContext: true,
                hasAccess: 'HomeView'
            },
            {
                context: APP_CONTEXT.VEHICLE,
                icon: 'fa-solid fa-bus-simple',
                tooltip: _translate('Vehicles'),
                resetOtherContext: true
            },
            {
                context: APP_CONTEXT.PUNCTUALITY,
                icon: 'fas fa-clock',
                tooltip: _translate('Punctuality'),
                resetOtherContext: true
            },
            {
                context: APP_CONTEXT.LINE,
                icon: 'fa fa-route',
                tooltip: _translate('Route'),
                resetOtherContext: true
            },
            {
                context: APP_CONTEXT.WARNING,
                icon: 'fa fa-exclamation-triangle',
                tooltip: _translate('Warnings'),
                resetOtherContext: true
            },
            {
                context: APP_CONTEXT.HISTORY,
                icon: 'fas fa-history',
                tooltip: _translate('History'),
                resetOtherContext: true
            },
            {
                context: APP_CONTEXT.TRAFFIC_DEVIATIONS,
                icon: 'fa-solid fa-file-lines',
                tooltip: _translate('Traffic Deviations'),
                resetOtherContext: false,
                hasAccess: 'TaWriter'
            }
        );

        this.bottomMenuItems = [
            {
                context: APP_CONTEXT.EDIT_TRAFFIC_DEVIATION_TEMPLATES,
                icon: 'fa-solid fa-file-pen',
                tooltip: _translate('Edit traffic deviation templates'),
                resetOtherContext: true,
                hasAccess: 'TemplateEditor'
            },
            {
                context: APP_CONTEXT.SETTINGS,
                icon: 'fa fa-cog',
                tooltip: _translate('Settings')
            }
        ];
    }

    private getEarliestDate(startDate: string, publicationStartDate: string | null): Date {  
        const startDateTime = new Date(startDate);
        const publicationStartDateTime = publicationStartDate ? new Date(publicationStartDate) : null;
        if (!publicationStartDateTime) return startDateTime;   
        return new Date(Math.min(publicationStartDateTime.getTime(), startDateTime.getTime()));
    }

    private getLatestDate(endDate: string, publicationEndDate: string | null): Date {  
        const endDateTime = new Date(endDate);
        const publicationEndDateTime = publicationEndDate ? new Date(publicationEndDate) : null;
        if (!publicationEndDateTime) return endDateTime;   
        return new Date(Math.max(publicationEndDateTime.getTime(), endDateTime.getTime()));
    }
}
